import React, { useEffect, useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { CONTROL_INPUT, MUTED_LABEL } from './scheduler-ui.js';
import { useSchedulerWrite } from './use-scheduler-write.js';
import type { SchedulerDriver, TerminalOption } from './use-scheduler-read-model.js';

interface SchedulerBulkAssignProps {
  roster: SchedulerDriver[];
  terminalOptions: TerminalOption[];
  onSuccess: () => void;
  initialTerminalId?: string | null;
}

export default function SchedulerBulkAssign({ roster, terminalOptions, onSuccess, initialTerminalId }: SchedulerBulkAssignProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [terminalId, setTerminalId] = useState('');
  const [assignmentType, setAssignmentType] = useState('terminal');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');

  const { bulkAssign, submitting, error, clearError } = useSchedulerWrite(() => {
    setShowForm(false);
    setSelectedIds(new Set());
    setTerminalId(initialTerminalId || '');
    setNotes('');
    onSuccess();
  });

  const realTerminals = terminalOptions.filter((t) => t.id !== 'all');

  useEffect(() => {
    if (initialTerminalId) {
      setShowForm(true);
      setAssignmentType('terminal');
      setTerminalId(initialTerminalId);
      clearError();
    }
  }, [initialTerminalId]);

  function toggleDriver(id: string | number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleSubmit() {
    if (selectedIds.size === 0) return;
    bulkAssign(
      Array.from(selectedIds),
      assignmentType === 'terminal' ? terminalId : null,
      assignmentType,
      startDate,
      notes || undefined,
    );
  }

  if (!showForm) {
    return (
      <button
        type="button"
        onClick={() => { setShowForm(true); clearError(); }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '10px 16px', borderRadius: 8,
          background: C.gold, color: '#111', border: 'none',
          fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 12,
        }}
      >
        Bulk Assign
      </button>
    );
  }

  const canSubmit = selectedIds.size > 0 && startDate && (assignmentType !== 'terminal' || terminalId) && !submitting;

  return (
    <div style={{ ...LAYOUT.subPanel, marginBottom: 12 }}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
        Bulk assign — {selectedIds.size} selected
      </div>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr 1fr', marginBottom: 12 }}>
        <div>
          <label style={MUTED_LABEL}>Assignment type</label>
          <select value={assignmentType} onChange={(e) => setAssignmentType(e.target.value)} style={CONTROL_INPUT}>
            <option value="terminal">Terminal</option>
            <option value="home">Home</option>
            <option value="off_rotation">Off rotation</option>
          </select>
        </div>
        {assignmentType === 'terminal' && (
          <div>
            <label style={MUTED_LABEL}>Terminal</label>
            <select value={terminalId} onChange={(e) => setTerminalId(e.target.value)} style={CONTROL_INPUT}>
              <option value="">Select...</option>
              {realTerminals.map((t) => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label style={MUTED_LABEL}>Start date</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={CONTROL_INPUT} />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={MUTED_LABEL}>Notes (optional)</label>
        <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Bulk assignment reason..." style={CONTROL_INPUT} />
      </div>

      <div style={{ maxHeight: 200, overflow: 'auto', marginBottom: 12, border: `1px solid ${C.borderSoft}`, borderRadius: 8, padding: 8 }}>
        {roster.map((driver) => (
          <label key={driver.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer', color: selectedIds.has(driver.id) ? C.text : C.textMuted, fontSize: 13 }}>
            <input type="checkbox" checked={selectedIds.has(driver.id)} onChange={() => toggleDriver(driver.id)} />
            {driver.first_name} {driver.last_name} ({driver.driver_id || driver.id})
          </label>
        ))}
      </div>

      {error && (
        <div style={{ color: C.red, fontSize: 13, marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: 'rgba(255,107,107,0.1)' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            padding: '10px 20px', borderRadius: 8,
            background: canSubmit ? C.gold : 'rgba(255,255,255,0.06)',
            color: canSubmit ? '#111' : 'rgba(255,255,255,0.2)',
            border: 'none', fontWeight: 700, fontSize: 13,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}
        >
          {submitting ? 'Assigning...' : `Assign ${selectedIds.size} drivers`}
        </button>
        <button
          type="button"
          onClick={() => { setShowForm(false); clearError(); setSelectedIds(new Set()); }}
          style={{ padding: '10px 16px', borderRadius: 8, background: 'none', color: C.textMuted, border: `1px solid ${C.borderSoft}`, fontSize: 13, cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
